import React, { useCallback, useEffect, useState } from 'react';
import {
  AiMode, EvidenceScope, WorkflowList, WorkflowState, WorkflowView, duplicateAutomationWorkflow, listAutomationWorkflows,
} from '../../../api/cafAutomation';
import { formatCount } from '../format';
import { Sparkline } from '../how/charts';
import { Icon } from '../Icon';
import { KebabMenu, MenuItem } from '../KebabMenu';
import { MSG_NOT_CONFIRMED, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { FORWARD_RULE_USED_BY, NOT_ENOUGH_EMAILS, STATE_LABEL, STATE_TONE } from './automationCopy';
import { RecentMatchesModal } from './RecentMatchesModal';
import { StepChips } from './StepChips';
import { ActivationConfirm, DeleteDraftConfirm, SeedsConfirm, StateConfirm, TeachTarget, UsedByModal } from './WorkflowConfirms';
import { WorkflowHistoryDrawer } from './WorkflowHistoryDrawer';

type Filter = 'all' | WorkflowState;
const FILTERS: ReadonlyArray<[Filter, string]> = [
  ['all', 'All'], ['on', 'On'], ['watching', 'Watching'], ['draft', 'Drafts'], ['paused', 'Paused'],
];

type Dialog =
  | { kind: 'watching' | 'paused' | 'delete' | 'matches' | 'history' | 'usedby' | 'activate' | 'approve'; id: string }
  | { kind: 'seeds' }
  | null;

/** How many built-in workflows the server ships (it sends only the count still missing). */
const SEED_TOTAL = 8;
const TURN_ON_LATER = 'Available after the next server update';
const CHECK_FIRST = 'Check its client emails first. Open it and preview each one.';

const lastDaysLabel = (view: WorkflowView, aiMode: AiMode): string => {
  if (view.state === 'on' && aiMode === 'on') return 'runs';
  return view.state === 'paused' ? 'before pausing' : 'would have run';
};

interface Props {
  days: number;
  reloadKey: number;
  onOpen: (workflowId: string | null) => void;
  onOpenTeach: (target: TeachTarget | null) => void;
  onResumeAll: () => void;
  onChanged: () => void;
  runsAvailable?: boolean;
  scope?: EvidenceScope;
}

export const WorkflowsCard: React.FC<Props> = ({ days, reloadKey, onOpen, onOpenTeach, onResumeAll, onChanged, runsAvailable = false, scope = 'caf-workflows-1' }) => {
  const toast = useToast();
  const [list, setList] = useState<WorkflowList | null>(null);
  const [problem, setProblem] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((count) => count + 1), []);
  const close = useCallback(() => setDialog(null), []);

  useEffect(() => {
    const controller = new AbortController();
    listAutomationWorkflows(days, controller.signal, scope).then(
      (next) => { if (!controller.signal.aborted) { setList(next); setProblem(''); } },
      () => { if (!controller.signal.aborted) setProblem("Couldn't load the workflows."); },
    );
    return () => controller.abort();
  }, [days, reloadKey, tick, scope]);

  const changed = () => { reload(); onChanged(); };
  const items = list ? list.items : [];
  const dialogId = dialog && dialog.kind !== 'seeds' ? dialog.id : null;
  // Looked up on every render, so a reload hands an open confirm the fresh revision (Reload keeps the note).
  const current = dialogId ? items.find((item) => item.workflow_id === dialogId) ?? null : null;

  const duplicate = async (view: WorkflowView) => {
    const result = await runSave(() => duplicateAutomationWorkflow(view.workflow_id));
    if (!result.ok) {
      if (result.message === MSG_NOT_CONFIRMED) reload();
      toast.failed(result.message, result.message === MSG_NOT_CONFIRMED ? undefined : () => { void duplicate(view); });
      return;
    }
    changed();
    const copy = result.value;
    toast.saved('Duplicated. The copy is a draft and no examples use it yet.',
      copy ? { label: 'Open it', onClick: () => onOpen(copy.workflow_id) } : undefined);
  };

  const menu = (view: WorkflowView): MenuItem[] => {
    const id = view.workflow_id;
    const entries: MenuItem[] = [{ key: 'open', label: 'Open', icon: 'pencil', onSelect: () => onOpen(id) }];
    if (view.state !== 'draft') {
      entries.push({ key: 'matches', label: 'See recent matches', icon: 'eye', onSelect: () => setDialog({ kind: 'matches', id }) });
    }
    if (view.state === 'watching' || view.state === 'paused') {
      entries.push({ key: 'on', label: 'Turn on…', icon: 'play', disabled: !runsAvailable,
        hint: !runsAvailable ? TURN_ON_LATER : view.needs_check ? CHECK_FIRST : undefined,
        onSelect: () => setDialog({ kind: 'activate', id }) });
    }
    if (view.state === 'on' && view.new_version_waiting) {
      entries.push({ key: 'approve', label: 'Approve new version…', icon: 'play', disabled: !runsAvailable,
        hint: !runsAvailable ? TURN_ON_LATER : undefined, onSelect: () => setDialog({ kind: 'approve', id }) });
    }
    if (view.state === 'on' || view.state === 'paused') {
      entries.push({ key: 'watching', label: 'Back to watching…', icon: 'eye', onSelect: () => setDialog({ kind: 'watching', id }) });
    }
    entries.push(
      { key: 'history', label: 'History', icon: 'clock', onSelect: () => setDialog({ kind: 'history', id }) },
      { key: 'duplicate', label: 'Duplicate', icon: 'copy', onSelect: () => { void duplicate(view); } },
    );
    if (view.state === 'on' || view.state === 'watching') {
      entries.push({ key: 'pause', label: 'Pause…', icon: 'pause', danger: true, separatorBefore: true, onSelect: () => setDialog({ kind: 'paused', id }) });
    }
    if (view.state === 'draft') {
      entries.push({ key: 'delete', label: 'Delete draft', icon: 'trash', danger: true, separatorBefore: true, onSelect: () => setDialog({ kind: 'delete', id }) });
    }
    return entries;
  };

  const usedBy = (view: WorkflowView) => {
    if (view.rule_keys.indexOf('cancelled_claim_forward') !== -1) return <span>{FORWARD_RULE_USED_BY}</span>;
    const count = view.used_by.length;
    if (count) {
      return (
        <button type="button" className="cur-linkish" onClick={() => setDialog({ kind: 'usedby', id: view.workflow_id })}>
          {`${count} ${count === 1 ? 'example' : 'examples'}`}
        </button>
      );
    }
    return <span className="cur-small"><strong>Not used yet.</strong> Attach it to an example in Teach the AI.</span>;
  };

  const lastDays = (view: WorkflowView, aiMode: AiMode) => {
    if (view.state === 'draft') return <span className="cur-muted">Not started</span>;
    if (aiMode === 'off') return <span className="cur-muted">The AI is off</span>;
    const total = view.last_days ? view.last_days.total : 0;
    return (
      <span className="cur-inline">
        <span className="cur-tnum">{`${formatCount(total)} ${lastDaysLabel(view, aiMode)}`}</span>
        {view.last_days && <Sparkline counts={view.last_days.counts} />}
      </span>
    );
  };

  const count = (key: Filter) => (key === 'all' ? items.length : items.filter((item) => item.state === key).length);
  const rows = filter === 'all' ? items : items.filter((item) => item.state === filter);
  const newButton = (
    <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" onClick={() => onOpen(null)}>
      <Icon name="plus" size="sm" />New workflow
    </button>
  );

  return (
    <section className="ui-card" aria-label="Workflows">
      <div className="ui-card__header">
        <div>
          <h2 className="ui-card__title">Workflows <span className="cur-tab-count">{list ? items.length : '—'}</span></h2>
          <p className="cur-card-sub">
            What happens when the AI follows an approved example. New workflows start in Watching. Attach them to examples in Teach the AI.
          </p>
        </div>
        {newButton}
      </div>
      <div className="cur-card-pad">
        {list?.workflows_paused && (
          <div className="cur-callout cur-callout--warn" role="status">
            <span className="cur-grow">All workflows are paused. Every workflow stopped before its next step. New unmatched decisions need manual handling.</span>
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={onResumeAll}>Resume workflows…</button>
          </div>
        )}
        {list && list.seeds_missing > 0 && (
          <div className="cur-callout cur-callout--info">
            <span className="cur-grow">{list.seeds_missing === SEED_TOTAL ? "The AI's built-in actions aren't set up as workflows yet." : `${list.seeds_missing} built-in ${list.seeds_missing === 1 ? 'workflow still needs' : 'workflows still need'} setting up.`}</span>
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => setDialog({ kind: 'seeds' })}>Set them up…</button>
          </div>
        )}
        {problem && (
          <div className="cur-alert cur-alert--error" role="alert">
            <span className="cur-grow">{problem}</span>
            <button type="button" className="ui-btn ui-btn--danger ui-btn--sm" onClick={reload}>Try again</button>
          </div>
        )}
        {items.length > 0 && (
          <div className="cur-chips" role="group" aria-label="Show workflows">
            {FILTERS.map(([key, label]) => (
              <button key={key} type="button" className="cur-chip" aria-pressed={filter === key} onClick={() => setFilter(key)}>
                {`${label} (${count(key)})`}
              </button>
            ))}
          </div>
        )}
      </div>
      {!list && !problem && <div className="cur-empty">Loading…</div>}
      {list && items.length > 0 && rows.length === 0 && <div className="cur-empty">None yet.</div>}
      {list && !items.length && (
        <div className="cur-empty">
          <strong>No workflows yet.</strong>
          Build one here, then attach it to examples in Teach the AI. New workflows start in Watching.
          <div style={{ marginTop: 10 }}>{newButton}</div>
        </div>
      )}
      {list && rows.length > 0 && (
        <div className="cur-table-wrap">
          <table className="cur-tbl" aria-label="Workflows">
            <colgroup>
              <col style={{ width: '24%' }} /><col style={{ width: '17%' }} /><col /><col style={{ width: 132 }} />
              <col style={{ width: 170 }} /><col style={{ width: 48 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Workflow</th><th>Used by</th><th>Steps</th><th>Status</th><th>{`Last ${days} days`}</th>
                <th><span className="cur-sr">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((view) => (
                <tr key={view.workflow_id}>
                  <td>
                    <div className="cur-who cur-workflow-summary">
                      <button type="button" className="cur-linkish cur-workflow-name" onClick={() => onOpen(view.workflow_id)}>{view.name}</button>
                      {(view.seed_key || view.built_in || view.settings_changed || view.catalogue_needs_review) && (
                        <div className="cur-workflow-meta">
                          {view.seed_key && <span className="cur-tag-builtin">Built-in</span>}
                          {view.built_in && (
                            <span className="cur-small cur-muted">
                              {view.built_in.percent != null
                                ? `Same as the built-in action ${view.built_in.percent}% of the time`
                                : NOT_ENOUGH_EMAILS}
                            </span>
                          )}
                          {view.settings_changed && <span className="cur-small cur-muted">Its emails differ from the current settings</span>}
                          {view.catalogue_needs_review && <span className="cur-pill cur-pill--amber">Email settings need review</span>}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{usedBy(view)}</td>
                  <td><StepChips steps={view.steps} /></td>
                  <td>
                    <span className={`cur-pill cur-pill--${STATE_TONE[view.state]}`}
                      title={view.state === 'paused' && view.pause_note ? view.pause_note : undefined}>{STATE_LABEL[view.state]}</span>
                    {view.new_version_waiting && <span className="cur-small"> · new version waiting for approval</span>}
                  </td>
                  <td>{lastDays(view, list.switch)}</td>
                  <td className="cur-right"><KebabMenu label={`Actions for ${view.name}`} items={menu(view)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog?.kind === 'seeds' && <SeedsConfirm onClose={close} onDone={changed} />}
      {current && dialog?.kind === 'watching' && (
        <StateConfirm view={current} to="watching" onClose={close} onDone={changed} onReload={reload} />
      )}
      {current && dialog?.kind === 'paused' && (
        <StateConfirm view={current} to="paused" onClose={close} onDone={changed} onReload={reload} />
      )}
      {current && dialog?.kind === 'delete' && (
        <DeleteDraftConfirm view={current} onClose={close} onDone={changed} onReload={reload} />
      )}
      {current && dialog?.kind === 'activate' && <ActivationConfirm view={current} overallMode={list?.switch ?? 'off'}
        onClose={close} onDone={changed} onReload={reload} />}
      {current && dialog?.kind === 'approve' && <ActivationConfirm view={current} overallMode={list?.switch ?? 'off'} approve
        onClose={close} onDone={changed} onReload={reload} />}
      {current && dialog?.kind === 'matches' && <RecentMatchesModal view={current} scope={scope} onClose={close} />}
      {current && dialog?.kind === 'history' && <WorkflowHistoryDrawer view={current} onClose={close} />}
      {current && dialog?.kind === 'usedby' && <UsedByModal view={current} onClose={close} onOpenTeach={onOpenTeach} />}
    </section>
  );
};
